import { GraphQLClient } from "graphql-request";
import {
  AuthRequestHeaders,
  createEmailAuthSession,
  getAuthHeaders,
} from "../auth/session";
import {
  GetArticlesQuery,
  getSdk,
  PhysicalOrderStatus,
  PhysicalStatus,
} from "./gen-types";
import { BASE_API_URL } from "../constants";

// export const BASE_API_URL = "https://api.ninfa.io:3000";

export const GQL_URL = `${BASE_API_URL}/v1/graphql`;
const graphQLClient = new GraphQLClient(GQL_URL);
const client = getSdk(graphQLClient);

export type ArticleListItem = GetArticlesQuery["articles"]["articles"][number];

const ARTICLES_PAGE_SIZE = 100;

export const signIn = async (walletAddress: string, token: string) => {
  const res = await client.SignIn(
    { walletAddress },
    {
      token: token,
      address: walletAddress,
    },
  );
  return res.user;
};

export const signInWithEmail = async (email: string, password: string) => {
  const normalizedEmail = email.trim().toLowerCase();
  const matchedUser = (
    await client.SearchUsers({ search: normalizedEmail })
  ).users.find((user) => user.email?.trim().toLowerCase() === normalizedEmail);

  if (!matchedUser) {
    return null;
  }

  const authHeaders = getAuthHeaders(
    createEmailAuthSession(normalizedEmail, password),
  );
  const res = await client.GetAuthenticatedUser(
    { userId: matchedUser.id },
    authHeaders,
  );

  return res.userDetails?.user ?? null;
};

export const searchUsers = async (search: string) => {
  const res = await client.SearchUsers({ search });
  return res.users;
};

export const getArtists = async () => {
  const res = await client.GetArtists();
  return res.users;
};

export const searchArtworks = async (search: string) => {
  const res = await client.SearchArtworks({ search });
  return res.searchNfts;
};

export const updateUserType = async (
  id: number,
  userType: string,
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.UpdateUserType({ id, userType }, authHeaders);
  return res.updateUserType;
};

export const updateYomType = async (
  id: number,
  yomType: string,
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.UpdateYomType({ id, yomType }, authHeaders);
  return res.updateYomType;
};

export const setHideArtwork = async (
  id: number,
  hidden: boolean,
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.HideNft(
    { id, hidden, type: "creator" },
    authHeaders,
  );
  return res.hideNft;
};

export const getTrades = async (authHeaders: AuthRequestHeaders) => {
  const res = await client.GetTrades(undefined, authHeaders);
  return res.trades;
};

export const getChartsData = async () => {
  const res = await client.GetChartsData();
  return res;
};

export const updateUserGenre = async (
  id: number,
  genre: string | null,
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.updateUserGenre({ id, genre }, authHeaders);
  return res.updateUser;
};

export const updateCanCreatePhysical = async (
  id: number,
  canCreatePhysical: boolean,
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.UpdateCanCreatePhysical(
    { id, canCreatePhysical },
    authHeaders,
  );
  return res.updateUser;
};

export const getLabs = async () => {
  const res = await client.getLabs();
  return res.labs;
};

export const updateLabsPage = async (
  payload: {
    id: number;
    pageLink?: string;
    image?: string;
    assetType?: string;
    orientation?: string;
  },
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.updateLabsPage(payload, authHeaders);
  return res.updateLabsPage;
};

export const getArtist = async (id: number) => {
  const res = await client.GetArtist({ id });
  return res.user;
};

export const createArticle = async (payload: {
  title: string;
  author: string;
  type: string;
  image1: string;
  image2?: string;
  artistId?: number;
  body: string;
  excerpt: string;
}) => {
  const res = await client.createArticle(payload);
  return res.createArticle;
};

export const updateArticle = async (payload: {
  id: number;
  title?: string;
  image1?: string;
  image2?: string;
  body?: string;
  excerpt?: string;
}) => {
  const res = await client.updateArticle(payload);
  return res.updateArticle;
};

export const getArticles = async () => {
  const articles: ArticleListItem[] = [];
  let offset = 0;
  let totalPages = 1;

  do {
    const res = await client.GetArticles({
      limit: ARTICLES_PAGE_SIZE,
      offset,
    });

    articles.push(...res.articles.articles);
    totalPages = res.articles.totalPages;
    offset += 1;
  } while (offset < totalPages);

  return articles;
};

export const getArticle = async (id: number) => {
  const res = await client.GetArticle({ id });
  return res.article;
};

export const deleteArticle = async (id: number) => {
  const res = await client.deleteArticle({ id });
  return res.deleteArticle;
};

export const getCollections = async () => {
  const res = await client.GetCollections();
  return res.collections;
};

export const getPhysicalsPage = async (payload?: {
  limit?: number;
  offset?: number;
  formatId?: number[];
  status?: PhysicalStatus[];
}) => {
  const res = await client.GetPhysicalsPage(payload);
  return res.getPhysicalsPage;
};

export const getPhysicalFormats = async () => {
  const res = await client.GetPhysicalFormats();
  return res.getPhysicalFormats;
};

export const searchPhysical = async (search: string) => {
  const res = await client.SearchPhysical({ search });
  return res.searchPhysical;
};

export const getPhysical = async (id: number) => {
  const res = await client.GetPhysical({ id });
  return res.getPhysical;
};

export const getPhysicalOrders = async (
  payload: {
    limit?: number;
    offset?: number;
    status?: PhysicalOrderStatus[];
    createdAtFrom?: string;
    createdAtTo?: string;
  },
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.GetPhysicalOrders(payload, authHeaders);
  return res.getPhysicalOrders;
};

export const updateCollection = async (
  payload: {
    id: number;
    title?: string;
    description?: string;
    banner?: string;
    brutto?: boolean;
    isCurrent?: boolean;
    interview?: number;
    article?: number;
    pass?: string | null;
    isCompressed?: boolean;
  },
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.updateCollection(payload, authHeaders);
  return res.updateCollection;
};

export const editPhysical = async (
  payload: {
    id: number;
    title?: string;
    description?: string;
    imageUrls?: {
      imageUrl: string;
      width: number;
      height: number;
    }[];
    totalSupply?: number;
    availableSupply?: number;
    price?: number;
    onSale?: boolean;
    isCurrent?: boolean;
    formatId?: number | null;
    status?: PhysicalStatus;
  },
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.EditPhysical(payload, authHeaders);
  return res.editOnSale;
};

export const createPhysical = async (
  payload: {
    title: string;
    description: string;
    creatorId: number;
    totalSupply?: number;
    availableSupply?: number;
    price?: number;
    formatId?: number;
    imageUrls: {
      imageUrl: string;
      width: number;
      height: number;
    }[];
    onSale?: boolean;
    assetLink: string;
  },
  authHeaders: AuthRequestHeaders,
) => {
  const res = await client.CreatePhysical(payload, authHeaders);
  return res.createPhysical;
};
